import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, MessageCircle, HelpCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FadeUp } from "@/components/Motion";
import { ServicesGrid } from "@/components/ServicesGrid";
import { FAQ } from "@/components/FAQ";
import { BookingForm } from "@/components/BookingForm";
import { ShareButtons } from "@/components/ShareButtons";
import { buildMeta, buildLinks, breadcrumbSchema, serviceSchema } from "@/lib/seo";
import { SERVICES } from "@/lib/services";
import { whatsappLink, SITE } from "@/lib/site";

export const Route = createFileRoute("/servicos/$slug")({
  loader: ({ params }) => {
    const service = SERVICES.find((s) => s.slug === params.slug);
    if (!service) throw notFound();
    return { service };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const s = loaderData.service;
    return {
      meta: buildMeta({
        title: s.metaTitle,
        description: s.metaDescription,
        path: `/servicos/${s.slug}`,
        image: `${SITE.url}${s.image}`,
      }),
      links: buildLinks(`/servicos/${s.slug}`),
      scripts: [
        breadcrumbSchema([
          { name: "Início", path: "/" },
          { name: "Serviços", path: "/servicos" },
          { name: s.shortTitle, path: `/servicos/${s.slug}` },
        ]),
        serviceSchema({ name: s.title, description: s.metaDescription, slug: s.slug }),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-6 py-32 text-center">
      <h1 className="font-display text-4xl">Tratamento não encontrado</h1>
      <p className="mt-3 text-muted-foreground">Veja todos os nossos tratamentos.</p>
      <Button asChild variant="hero" className="mt-6">
        <Link to="/servicos">Ver tratamentos</Link>
      </Button>
    </div>
  ),
  component: ServiceDetail,
});

function ServiceDetail() {
  const { service } = Route.useLoaderData();
  return (
    <>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.image}
            alt={service.imageAlt}
            className="h-full w-full object-cover"
            width={1200}
            height={800}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
          <FadeUp>
            <nav className="mb-5 flex items-center gap-2 text-sm text-white/80">
              <Link to="/" className="hover:text-white">
                Início
              </Link>
              <span>/</span>
              <Link to="/servicos" className="hover:text-white">
                Serviços
              </Link>
              <span>/</span>
              <span className="text-white">{service.shortTitle}</span>
            </nav>
            <h1 className="max-w-3xl font-display text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
              {service.title}
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-white/90">{service.excerpt}</p>
          </FadeUp>
        </div>
      </section>

      <section className="bg-background py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-3 lg:items-start">
            <div className="lg:col-span-2">
              <FadeUp>
                <div className="prose prose-lg max-w-none prose-headings:font-display prose-p:text-foreground/80">
                  <h2 className="flex items-center gap-3 text-3xl">
                    <Info className="h-8 w-8 text-primary" />
                    Sobre o Tratamento
                  </h2>
                  <p className="text-xl leading-relaxed">{service.description}</p>

                  <h3 className="mt-12 text-2xl font-bold">Como é feito o atendimento?</h3>
                  <p>{service.howItWorks}</p>

                  <div className="mt-8 grid gap-4 sm:grid-cols-2">
                    {service.extraImages.map((img: string, idx: number) => (
                      <div key={idx} className="overflow-hidden rounded-2xl shadow-elegant">
                        <img
                          src={img}
                          alt={`Detalhe do atendimento ${service.shortTitle}`}
                          className="h-48 w-full object-cover transition-smooth hover:scale-105"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </FadeUp>

              <FadeUp delay={0.1} className="mt-16">
                <h2 className="mb-8 flex items-center gap-3 font-display text-3xl font-bold">
                  <HelpCircle className="h-8 w-8 text-primary" />
                  Perguntas Frequentes
                </h2>
                <FAQ items={service.faqs} />
              </FadeUp>
            </div>

            <aside className="space-y-8 lg:sticky lg:top-24">
              <FadeUp delay={0.2}>
                <div className="rounded-3xl bg-gradient-card p-8 shadow-card border border-primary/10">
                  <ShareButtons 
                    title={service.title} 
                    path={`/servicos/${service.slug}`} 
                    className="mb-8"
                  />
                  <h2 className="font-display text-2xl font-bold">Benefícios</h2>
                  <ul className="mt-6 space-y-4">
                    {service.benefits.map((b: string) => (
                      <li key={b} className="flex items-start gap-3">
                        <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-primary" />
                        <span className="text-foreground/85">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeUp>

              <FadeUp delay={0.3}>
                <div className="rounded-3xl bg-primary/5 p-8 border border-primary/20">
                  <h3 className="mb-4 font-display text-xl font-bold">Agende Agora</h3>
                  <p className="mb-6 text-sm text-muted-foreground">
                    Preencha o formulário abaixo para solicitar seu agendamento via WhatsApp.
                  </p>
                  <BookingForm defaultService={service.shortTitle} />
                </div>
              </FadeUp>
            </aside>
          </div>
        </div>
      </section>

      <section className="bg-secondary/30 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <h2 className="mb-10 text-center font-display text-3xl font-bold sm:text-4xl">
              Outros tratamentos
            </h2>
          </FadeUp>
          <ServicesGrid limit={4} />
        </div>
      </section>
    </>
  );
}
